import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { collection, doc, writeBatch, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { allSubcategories } from '../../global/categories';
import { selectAllFinancialCategories } from '../../store/userDataSlice';
import { useUserContext } from '../../contexts/UserContext';
import logger from '../../utils/logger';

const CategorySync = () => {
    const { userId } = useUserContext();
    const userCategories = useSelector(selectAllFinancialCategories);
    const hasRunRef = useRef(false);

    useEffect(() => {
        // Only run once per session to prevent duplicate creation
        if (!userId || !userCategories || hasRunRef.current) return;

        const checkAndSync = async () => {
            const missing = allSubcategories.filter(defCat =>
                !userCategories.some(userCat => userCat.name === defCat.name && userCat.category === defCat.group?.toUpperCase())
            );

            if (missing.length > 0) {
                logger.debug("Found missing categories to sync:", missing.length);

                // Double-check in Firebase to avoid race conditions
                const batch = writeBatch(db);
                let count = 0;

                for (const cat of missing) {
                    const groupCollection = cat.group?.toLowerCase();
                    if (!groupCollection) continue;

                    // Check if category with this name already exists in Firebase
                    const collRef = collection(db, "settings", userId, groupCollection);
                    const q = query(collRef, where("name", "==", cat.name));
                    const existing = await getDocs(q);

                    if (existing.empty) {
                        // Only create if it truly doesn't exist
                        const ref = doc(collRef);
                        batch.set(ref, {
                            ...cat,
                            id: ref.id,
                        });
                        count++;
                    }
                }

                if (count > 0) {
                    try {
                        await batch.commit();
                        logger.info(`Synced ${count} missing categories.`);
                        hasRunRef.current = true;
                    } catch (e) {
                        logger.error("Failed to sync categories", e);
                    }
                } else {
                    hasRunRef.current = true;
                }
            } else {
                hasRunRef.current = true;
            }
        };

        checkAndSync();
    }, [userId, userCategories]);

    return null;
};

export default CategorySync;
